import React, { useEffect, useState } from "react"
import { ItemPicker, SelectedSortingOption } from "@tenforce/product-fields"

import { getItemAncestry } from "../../../../../apis/selectors"
import { IItemPickerField } from "../../.."
import { useAppDispatch, useAppSelector } from "../../../../../hooks"
import { GlobalState } from "../../../../../store"
import HTMLAndInternalLinkParser from "../../common/HTMLAndInternalLinkParser"
import { Labels } from "../../../../../util/Translations"
import { addErrorNotification } from "../../../../notifications/actions"
import { getItemDetailUrl } from "../../../../../util/UrlGenerator"
import env from "../../../../../env"
import {
    fetchAvailableSortingFields,
    fetchCollectionItems as fetchCollectionItemsThunk,
    fetchConceptFields,
    fetchVisibleFields,
    updateVisibleFields,
} from "../../../../collection/slice"
import { FetchCollectionParams, Sorting } from "../../../../collection"
import { Field } from "../../Field"
import { IItem } from "../../../../../models"
import { formatString } from "../../../../../util/Strings"
import { Symbols } from "@tenforce/toolbox-fontmap"
import { PAGE_SIZE } from "../../../../thumbtableview/slice"
import { updateField } from "../../../slice"

type Props = IItemPickerField

// TODO: These are just to trick tailwind into generating the dynamically created classes from the product-fields.
// It should at some point be removed once the SCSS is generated properly.
const _levelOne = "tw-ml-0"
const _levelTwo = "tw-ml-4"
const _levelThree = "tw-ml-8"
const _levelFour = "tw-ml-12"
const _levelFive = "tw-ml-16"

const ItemPickerNew: React.FC<Props> = (props: Props) => {
    const dispatch = useAppDispatch()
    const [itemsWithParents, setItemsWithParents] = useState<IItem[]>([])
    const [selectedItemsIds, setSelectedItemsIds] = useState<number[]>([])
    const [isSortingLoading, setIsSortingLoading] = useState<boolean>(true)
    const [isFieldSelectionLoading, setIsFieldSelectionLoading] =
        useState<boolean>(true)
    const {
        description,
        isSingleSelect,
        value: selectedItems,
        isDisabled,
        itemId,
        fieldId,
        title,
        odataType,
        isLoading = false,
        ...rest
    } = props

    const { isLoading: globalIsLoading, currentFieldInEditMode = "" } =
        useAppSelector((state: GlobalState) => state.fields)

    const {
        visibleFields,
        isQueryProcessing,
        visibleFieldsIds,
        selectedSorting = [],
        availableSortingOptions,
        selectableFieldsData,
        items,
        remainingCount = 0,
    } = useAppSelector((state: GlobalState) => state.collections[fieldId] || {})

    const fetchAncestry = async (itemId: number): Promise<IItem[]> => {
        try {
            let parents = await getItemAncestry(itemId)
            if (parents.length > 0) {
                parents = parents.map((parent) => ({
                    ...parent,
                    url: getItemDetailUrl(env.applicationPath, "", parent.id),
                }))
            }
            return parents
        } catch (error) {
            dispatch(
                addErrorNotification(
                    error instanceof Error
                        ? error.message
                        : "Something went wrong when fetching ancestry.",
                ),
            )
            return []
        }
    }

    useEffect(() => {
        const fetchParentsForItems = async (): Promise<void> => {
            const updatedItems = []
            const selectedIds: number[] = []
            for (const item of selectedItems) {
                selectedIds.push(item.id)
                const parentsData = await fetchAncestry(item.id)
                updatedItems.push({
                    ...item,
                    url: getItemDetailUrl(env.applicationPath, "", item.id),
                    parents: parentsData,
                })
            }
            setSelectedItemsIds(selectedIds)
            setItemsWithParents(updatedItems)
        }
        fetchParentsForItems()
    }, [selectedItems])

    const isFieldDisabled =
        isDisabled || !["", fieldId].includes(currentFieldInEditMode)

    const fetchCollectionData = async (): Promise<void> => {
        await dispatch(fetchVisibleFields({ itemId, fieldId, odataType }))
        await fetchCollectionItems()
    }

    const fetchCollectionItems = async (): Promise<void> => {
        await dispatch(
            fetchCollectionItemsThunk({
                itemId,
                fieldId,
                odataType,
                offset: 0,
                limit: PAGE_SIZE,
            }),
        )
    }

    const fetchMoreCollectionItems = async (): Promise<void> => {
        await dispatch(
            fetchCollectionItemsThunk({
                itemId,
                fieldId,
                odataType,
                offset: items.length,
                limit: PAGE_SIZE,
            }),
        )
    }

    const fetchSortingFields = async (): Promise<void> => {
        setIsSortingLoading(true)
        await dispatch(
            fetchAvailableSortingFields({ itemId, fieldId, odataType }),
        )
        setIsSortingLoading(false)
    }

    const fetchSelectableFields = async (): Promise<void> => {
        setIsFieldSelectionLoading(true)
        await dispatch(
            fetchConceptFields({
                fieldId,
                filter: "",
                top: 20,
                skip: 0,
            }),
        )
        setIsFieldSelectionLoading(false)
    }

    const onSaveViewSettings = async (settingsData: {
        selectedFields?: string[]
        sorting?: SelectedSortingOption[]
    }): Promise<void> => {
        const apiPayload: FetchCollectionParams = {
            itemId,
            fieldId,
            odataType,
            selectedFields: settingsData.selectedFields
                ? settingsData.selectedFields
                : visibleFieldsIds,
            sorting:
                settingsData?.sorting?.map(
                    (option: SelectedSortingOption): Sorting => {
                        return {
                            direction: option.mode,
                            fieldDefinitionId: option.columnId,
                        }
                    },
                ) ?? [],
        }

        await dispatch(updateVisibleFields(apiPayload)).unwrap()
        fetchCollectionData()
    }

    const onFilterChange = (filter: string): void => {
        dispatch(
            fetchConceptFields({
                fieldId,
                filter,
                top: 20,
                skip: 0,
            }),
        )
    }

    const loadMoreSelectableValue = (filter: string): void => {
        dispatch(
            fetchConceptFields({
                fieldId,
                filter,
                top: 20,
                skip: selectableFieldsData.currentSkip,
            }),
        )
    }

    const _onSave = async (itemIds: number[]): Promise<void> => {
        await dispatch(
            updateField({
                field: props,
                newValue: itemIds,
            }),
        ).unwrap()
    }

    const {
        ClickHereToEdit,
        Columns,
        FilterAllNValues,
        FilterNSelectedValues,
        ItemOverviewSorting,
        ItemPickerAddItem,
        ItemPickerAddItems,
        ItemPickerEditItem,
        ItemPickerEditItems,
        LoadNMore,
        LoadNMoreOf,
        NoItems,
        NothingFound,
        OpenViewSettings,
        ShowAllValues,
        ShowSelectedValues,
        SortLevelOne,
        SortLevelThree,
        SortLevelTwo,
        ViewSettings,
    } = Labels

    return (
        <ItemPicker
            {...rest}
            isDisabled={isFieldDisabled}
            description={
                <HTMLAndInternalLinkParser
                    content={(description as string) ?? ""}
                />
            }
            fieldId={fieldId}
            title={title}
            isLoading={globalIsLoading || isLoading}
            isSingleSelect={isSingleSelect}
            labels={{
                AddItem: ItemPickerAddItem,
                AddItems: ItemPickerAddItems,
                EditItem: ItemPickerEditItem,
                EditItems: ItemPickerEditItems,
                EmptyPlaceholder: ClickHereToEdit,
                FieldSelectionSectionLabel: Columns,
                FilterAllNValues: (count) =>
                    Symbols.Search + formatString(FilterAllNValues, count),
                FilterNSelectedValues: (count) =>
                    Symbols.Search + formatString(FilterNSelectedValues, count),
                FilterNoResults: NothingFound,
                LoadNMore: (remaining) => formatString(LoadNMore, remaining),
                LoadNMoreOf: (pageSize, remaining) =>
                    formatString(LoadNMoreOf, pageSize, remaining),
                NoItems: NoItems,
                OpenViewSettings: OpenViewSettings,
                ShowAllValuesTooltip: ShowAllValues,
                ShowSelectedValuesTooltip: ShowSelectedValues,
                SortLevelOne: SortLevelOne,
                SortLevelThree: SortLevelThree,
                SortLevelTwo: SortLevelTwo,
                SortingSectionLabel: ItemOverviewSorting,
                ViewSettings: ViewSettings,
            }}
            value={itemsWithParents}
            collection={{
                visibleFields: visibleFields,
                selectedItemIds: selectedItemsIds,
                visibleFieldsIds: visibleFieldsIds,
                isQueryProcessing: isQueryProcessing || isLoading,
                items: items,
                remainingCount: remainingCount,
            }}
            fieldSelection={{
                isLoading: isFieldSelectionLoading,
                onFilterChange: onFilterChange,
                onLoadMore: loadMoreSelectableValue,
                selectableFields: {
                    data: selectableFieldsData?.allConceptFields || [],
                    totalCount: selectableFieldsData?.totalValueCount || 0,
                    currentSkip: selectableFieldsData?.currentSkip || 0,
                    hasMore: selectableFieldsData?.hasMore || false,
                },
                fetchOptions: fetchSelectableFields,
            }}
            sorting={{
                // TODO: Replace
                totalAvailableSortingOptions: 1000,
                isLoading: isSortingLoading,
                selectedSorting: selectedSorting.map((sorting) => {
                    return {
                        columnId: sorting.fieldDefinitionId,
                        mode: sorting.direction,
                    }
                }),
                selectableSortingOptions: availableSortingOptions,
                fetchOptions: fetchSortingFields,
            }}
            fetchCollectionData={fetchCollectionData}
            onSaveViewSettings={onSaveViewSettings}
            getItemField={(itemField): JSX.Element => {
                return <Field field={itemField} isLoading={false} />
            }}
            onSave={_onSave}
            onLoadMore={fetchMoreCollectionItems}
        />
    )
}

export default ItemPickerNew
